import { RegistrationRequest, UploadData } from '../../../../../../store/uploadStore';
import { calcHash } from '../../../../../../libs/file';
import { FileWithPath } from 'react-dropzone';

/**
 * There are 3 places where we store photo state:
 * - Registration (PUT request with basic photo info to let the server know how many photos will be uploaded)
 * - Upload queue (to POST files to storage proxy and then send a PUT request with validation, camera and full details per each individual photo)
 * - Upload state (stores already validated, uploaded and re-registered photos; gets restored after page reload, see `restoreUploadState`)
 */
const getFileHashesFromStateAndQueues = (
  upload: UploadData | undefined,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
  includeRegisteredButNonUploaded: boolean,
) => {
  const fileHashes = new Set<string>();

  if (upload) {
    upload.queue?.forEach((photo) => fileHashes.add(calcHash(photo.file)));

    upload.upload?.photos.forEach((photo) => {
      if (includeRegisteredButNonUploaded || photo.uploaded) {
        fileHashes.add(photo.hash);
      }
    });
  }

  registrationRequests?.forEach((request) => {
    request.fileHashes?.forEach((fileHash) => fileHashes.add(fileHash));
  });

  return Array.from(fileHashes);
};

export const filterDuplicateFiles = (files: FileWithPath[]) => {
  const seenHashes = new Set<string>();
  const uniqueFiles: FileWithPath[] = [];

  for (const file of files) {
    const hash = calcHash(file);
    if (!seenHashes.has(hash)) {
      seenHashes.add(hash);
      uniqueFiles.push(file);
    }
  }

  return uniqueFiles;
};

export const filterNonUploadedFiles = (
  files: FileWithPath[],
  upload: UploadData | undefined,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
) => {
  const existingHashes = getFileHashesFromStateAndQueues(upload, registrationRequests, false);

  return files.filter((file) => !existingHashes.includes(calcHash(file))).sort((a, b) => a.name.localeCompare(b.name));
};

export const filterNewFiles = (
  files: FileWithPath[],
  upload: UploadData | undefined,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
) => {
  const existingHashes = getFileHashesFromStateAndQueues(upload, registrationRequests, true);

  return files.filter((file) => !existingHashes.includes(calcHash(file)));
};

export const calculateTotal = (
  files: FileWithPath[],
  upload: UploadData | undefined,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
) => {
  const existingHashes = getFileHashesFromStateAndQueues(upload, registrationRequests, true);
  const newFiles = files.filter((file) => !existingHashes.includes(calcHash(file)));

  return existingHashes.length + newFiles.length;
};
